import React from "react";
import {
  Box,
  Button,
  Typography,
  TextField,
  IconButton,
  InputAdornment,
  Autocomplete,
  Alert,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";

const ProductSelectionStep = ({
  inputValue,
  onInputChange,
  searchResults,
  loading,
  selectedProduct,
  onSelectProduct,
  onClearProduct,
  onNext,
  membershipLoading,
  membershipError,
  blockingMessage,
  nextLabel = "Next",
}) => {
  return (
    <>
      <Box sx={{ marginBottom: 2 }}>
        <Typography variant="body1" sx={{ fontSize: { xs: 16, sm: 16, md: 18 } }}>
          Search for the product you wish to add
        </Typography>
      </Box>
      <Box sx={{ marginBottom: 4, marginTop: 2 }}>
        <Autocomplete
          options={searchResults}
          getOptionLabel={(option) => option.displayName || ""}
          isOptionEqualToValue={(option, value) =>
            option.productFormatId === value?.productFormatId
          }
          inputValue={inputValue}
          onInputChange={(event, newInputValue, reason) => {
            if (reason === "reset" && selectedProduct) return;
            onInputChange({ target: { value: newInputValue } });
          }}
          value={selectedProduct}
          onChange={(event, newValue) => {
            if (newValue) {
              onSelectProduct(newValue);
            } else {
              onClearProduct();
            }
          }}
          loading={loading}
          noOptionsText={inputValue ? "No products found" : "Start typing to search"}
          renderOption={(props, option) => (
            <Box
              component="li"
              {...props}
              sx={{
                display: "flex !important",
                justifyContent: "space-between !important",
                alignItems: "center !important",
                gap: 2,
              }}
            >
              <Typography variant="body1" sx={{ flex: 1 }}>
                {option.displayName}
              </Typography>
              <AddCircleOutlineIcon fontSize="small" sx={{ flexShrink: 0 }} />
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              placeholder="Search for product names"
              sx={{
                "& legend": { display: "none" },
                "& fieldset": { top: 0 },
                "& .MuiOutlinedInput-root": {
                  background: (theme) => theme.palette.pure.white,
                },
                "& input::-webkit-clear-button, & input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                  {
                    display: "none",
                  },
              }}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    <InputAdornment position="start" sx={{ marginLeft: -0.5 }}>
                      <SearchIcon />
                    </InputAdornment>
                    {params.InputProps.startAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      </Box>

      <Box sx={{ marginBottom: 4 }}>
        <Typography variant="body2" sx={{ marginBottom: 1, fontWeight: 600 }}>
          Selected Product
        </Typography>
        <Box
          sx={{
            minHeight: 88,
            backgroundColor: (theme) => theme.palette.terrain[300],
            borderRadius: 1,
            display: "flex",
            alignItems: "center",
            padding: 2,
          }}
        >
          {selectedProduct ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
                backgroundColor: (theme) => theme.palette.terrain[50],
                borderRadius: 1,
                padding: 2,
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {selectedProduct.productName || selectedProduct.displayName}
                </Typography>
                {selectedProduct.formatName ? (
                  <Typography variant="body2" color="text.secondary">
                    {selectedProduct.formatName}
                  </Typography>
                ) : null}
              </Box>
              <IconButton size="small" onClick={onClearProduct} color="error">
                <CloseIcon />
              </IconButton>
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flex: 1,
              }}
            >
              <Typography variant="body1" fontWeight={600}>
                No Product Selected Yet...
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {blockingMessage ? (
        <Box sx={{ marginBottom: 3 }}>
          <Alert severity="warning">{blockingMessage}</Alert>
        </Box>
      ) : null}

      {membershipError ? (
        <Box sx={{ marginBottom: 3 }}>
          <Alert severity="error">{membershipError}</Alert>
        </Box>
      ) : null}

      <Box sx={{ marginTop: 4 }}>
        <Button
          variant="contained"
          onClick={onNext}
          disabled={!selectedProduct || membershipLoading || !!blockingMessage}
          fullWidth
          sx={{ height: 48 }}
        >
          {membershipLoading ? <CircularProgress size={20} /> : nextLabel}
        </Button>
      </Box>
    </>
  );
};

export default ProductSelectionStep;
